import { useState, useEffect } from 'react';
import { Button, message, Upload, Modal, Cascader, Input, Select } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { LoadingOutlined, PlusOutlined } from '@ant-design/icons';
import type { UploadChangeParam } from 'antd/es/upload';
import type { RcFile, UploadFile, UploadProps } from 'antd/es/upload/interface';
import type { DefaultOptionType } from 'antd/es/cascader';
import { getUploadConfig } from '@/https/upload';
import Https from '@/https';
import './DeviceType.css';


export default function CreateType() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  const [messageApi, messageContextHolder] = message.useMessage();
  const UploadConfig = getUploadConfig();
  const back = () => {
    navigate(-1);
    dispatch({
      type: 'POP_MENU_KEYPATH',
      payload: { keyPath: 1, selectedKeys: 1 }
    })
  }

  const [loadingFront, setLoadingFront] = useState(false);
  const [frontUrl, setFrontUrl] = useState<string>();
  const [frontFileList, setFrontFileList] = useState<UploadFile[]>([]);

  const beforeUpload = (file: RcFile) => {
    const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';
    if (!isJpgOrPng) {
      messageApi.error('只能上传JPG/PNG文件!');
    }
    const isLt20M = file.size / 1024 / 1024 < 20;
    if (!isLt20M) {
      messageApi.error('图片大小不能超过20MB!');
    }
    return isJpgOrPng && isLt20M;
  };

  const handleFrontChange: UploadProps['onChange'] = (info: UploadChangeParam<UploadFile>) => {
    if (info.file.status === 'uploading') {
      setLoadingFront(true);
      setFrontFileList([
        {
          uid: info.file.uid,
          name: info.file.name,
          status: 'uploading',
          url: '',
        }
      ])
      return;
    }
    if (info.file.status === 'done') {
      // Get this url from response in real world.
      if(info.fileList[0].response) {
        setFrontFileList([
          {
            uid: info.file.uid,
            name: info.file.name,
            status: info.fileList[0].status,
            url: info.fileList[0].response[0],
          }
        ])
        setFrontUrl(info.fileList[0].response[0]);
        setLoadingFront(false);
      }
    }
  };

  const handleFrontRemove = () => {
    setFrontUrl('');
    setFrontFileList([]);
    setLoadingFront(false);
  }

  const [loadingBack, setLoadingBack] = useState(false);
  const [backUrl, setBackUrl] = useState<string>();
  const [backFileList, setBackFileList] = useState<UploadFile[]>([]);

  const handleBackChange: UploadProps['onChange'] = (info: UploadChangeParam<UploadFile>) => {
    if (info.file.status === 'uploading') {
      setLoadingBack(true);
      setBackFileList([
        {
          uid: info.file.uid,
          name: info.file.name,
          status: 'uploading',
          url: '',
        }
      ])
      return;
    }
    if (info.file.status === 'done') {
      if(info.fileList[0].response) {
        setBackUrl(info.fileList[0].response[0]);
        setBackFileList([
          {
            uid: info.file.uid,
            name: info.file.name,
            status: info.fileList[0].status,
            url: info.fileList[0].response[0],
          }
        ])
        setLoadingBack(false);
      }
    }
  };

  const handleBackRemove = () => {
    setBackUrl('');
    setBackFileList([]);
    setLoadingBack(false);
  }

  const [loadingSide, setLoadingSide] = useState(false);
  const [sideUrl, setSideUrl] = useState<string>();
  const [sideFileList, setSideFileList] = useState<UploadFile[]>([]);

  const handleSideChange: UploadProps['onChange'] = (info: UploadChangeParam<UploadFile>) => {
    if (info.file.status === 'uploading') {
      setSideFileList([
        {
          uid: info.file.uid,
          name: info.file.name,
          status: 'uploading',
          url: '',
        }
      ])
      setLoadingSide(true);
      return;
    }
    if (info.file.status === 'done') {
      if(info.fileList[0].response) {
        setSideUrl(info.fileList[0].response[0]);
        setSideFileList([
          {
            uid: info.file.uid,
            name: info.file.name,
            status: info.fileList[0].status,
            url: info.fileList[0].response[0],
          }
        ])
        setLoadingSide(false);
      }
    }
  };

  const handleSideRemove = () => {
    console.log('handleSideRemove')
    setSideUrl('');
    setSideFileList([]);
    setLoadingSide(false);
  }

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [previewTitle, setPreviewTitle] = useState('');
  const handleCancel = () => setPreviewOpen(false);

  const getBase64 = (file: RcFile): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = error => reject(error);
  });
  const handlePreview = async (file: UploadFile) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj as RcFile);
    }
    setPreviewImage(file.url || (file.preview as string));
    setPreviewOpen(true);
    setPreviewTitle(file.name || file.url!.substring(file.url!.lastIndexOf('/') + 1));
  };

  const [deviceType, setDeviceType] = useState<string[]>([]);
  const [deviceTypeOptions, setDeviceTypeOptions] = useState<DefaultOptionType[]>([]);
  const getDeviceTypeOptions = () => {
    Https.post({
      url: 'Api/device/V1/deviceEnum/FindDeviceTypeEnumList',
      data: {}
    }).then((res) => {
      if(res.state === 1) {
        let list = res.data;
        setDeviceTypeOptions(list.map((item: any) => {
          return {
            value: item.value,
            label: item.text,
            children: item.children ? item.children.map((child: any) => {
              return {
                value: child.value,
                label: child.text
              }
            }) : []
          }
        }));
      }
    })
  }

  const deviceTypeChange = (value: string[]) => {
    setDeviceType(value);
  }

  const [name, setName] = useState('');
  const onNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
  }

  const [deviceFactory, setDeviceFactory] = useState<string|undefined>(undefined);
  const [deviceFactoryList, setDeviceFactoryList] = useState<any[]>([]);
  const getDeviceFactoryList = () => {
    Https.post({
      url: 'Api/device/V1/deviceEnum/FindDeviceFactoryEnumList',
      data: {}
    }).then((res) => {
      if(res.state === 1) {
        setDeviceFactoryList(res.data.map((item: any) => {
          return {
            value: item.value,
            label: item.text
          }
        }));
      }
    })
  }

  const deviceFactoryChange = (value: string) => {
    setDeviceFactory(value);
  }

  const createConfirm = () => {
    if(!frontUrl) {
      messageApi.warning('请上传正面图片');
      return;
    }
    if(!backUrl) {
      messageApi.warning('请上传背面图片');
      return;
    }
    if(!sideUrl) {
      messageApi.warning('请上传侧面图片');
      return;
    }
    if(!name) {
      messageApi.warning('请输入设备型号名称');
      return;
    }
    if(!deviceType.length) {
      messageApi.warning('请选择设备类型');
      return;
    }
    if(!deviceFactory) {
      messageApi.warning('请选择设备厂家');
      return;
    }
    Https.post({
      url: 'Api/device/V1/DeviceModel/Update',
      data: {
        modelId: location.state.model,
        name: name,
        deviceFactory: deviceFactory,
        deviceType: deviceType.join(','),
        frontUrl: frontUrl,
        backUrl: backUrl,
        sideUrl: sideUrl,
        isActive: 1,
      }
    }).then(res => {
      if(res.state === 1) {
        messageApi.success('编辑成功');
        setTimeout(() => {
          back();
        }, 1000);
      } else {
        messageApi.error(res.data.errorMessage);
      }
    })
  }

  const getDetail = () => {
    Https.post({
      url: 'Api/device/V1/DeviceModel/Detail',
      data: {
        id: location.state.model,
      }
    }).then(res => {
      if(res.state === 1) {
        setFrontUrl(res.data.frontUrl);
        setFrontFileList([
          {
            uid: '-1',
            name: 'front.png',
            status: 'done',
            url: res.data.frontUrl,
          }
        ])
        setBackUrl(res.data.backUrl);
        setBackFileList([
          {
            uid: '-1',
            name: 'back.png',
            status: 'done',
            url: res.data.backUrl,
          }
        ])
        setSideUrl(res.data.sideUrl);
        setSideFileList([
          {
            uid: '-1',
            name: 'side.png',
            status: 'done',
            url: res.data.sideUrl,
          }
        ])
        setName(res.data.name);
        setDeviceType([res.data.deviceType]);
        setDeviceFactory(res.data.deviceFactory);
      } else {
        messageApi.error(res.data.errorMessage || res.errorMessage);
      }
    })
  }

  useEffect(() => {
    getDeviceFactoryList();
    getDeviceTypeOptions();
    getDetail();
  }, [])

  return (
    <section className="DeviceType-Create">
      <Button onClick={back} size="small" className="back-btn">返回</Button>
      <section className="create-box">
        <section className="type-imgs">
          <section className="type-img">
            <header className="required">正面</header>
            <Upload
              name="file"
              fileList={frontFileList}
              listType="picture-card"
              maxCount={1}
              className="avatar-uploader"
              showUploadList={true}
              action={UploadConfig.action}
              headers={UploadConfig.headers}
              beforeUpload={beforeUpload}
              onChange={handleFrontChange}
              onRemove={handleFrontRemove}
              onPreview={handlePreview}>
              { frontUrl ? null :
                <section>
                  {loadingFront ? <LoadingOutlined /> : <PlusOutlined />}
                  <section style={{ marginTop: 8 }}>上传图片</section>
                </section>
              }
            </Upload>
          </section>
          <section className="type-img">
            <header className="required">背面</header>
            <Upload
              name="file"
              fileList={backFileList}
              listType="picture-card"
              maxCount={1}
              className="avatar-uploader"
              showUploadList={true}
              action={UploadConfig.action}
              headers={UploadConfig.headers}
              beforeUpload={beforeUpload}
              onChange={handleBackChange}
              onRemove={handleBackRemove}
              onPreview={handlePreview}>
              { backUrl ? null :
                <section>
                  {loadingBack ? <LoadingOutlined /> : <PlusOutlined />}
                  <section style={{ marginTop: 8 }}>上传图片</section>
                </section>
              }
            </Upload>
          </section>
          <section className="type-img">
            <header className="required">侧面</header>
            <Upload
              name="file"
              fileList={sideFileList}
              listType="picture-card"
              maxCount={1}
              className="avatar-uploader"
              showUploadList={true}
              action={UploadConfig.action}
              headers={UploadConfig.headers}
              beforeUpload={beforeUpload}
              onChange={handleSideChange}
              onRemove={handleSideRemove}
              onPreview={handlePreview}>
              { sideUrl ? null :
                <section>
                  {loadingSide ? <LoadingOutlined /> : <PlusOutlined />}
                  <section style={{ marginTop: 8 }}>上传图片</section>
                </section>
              }
            </Upload>
          </section>
        </section>
        <section className="deviceType-info">
          <section className="type-info-item">
            <span className="label required">设备类型</span>
            <Cascader
              allowClear
              value={deviceType}
              options={deviceTypeOptions}
              onChange={(value) => deviceTypeChange(value as string[])}
              style={{ width: 200 }}
              placeholder="请选择设备型号"
            />
          </section>
          <section className="type-info-item">
            <span className="label required">型号名称</span>
            <Input placeholder="请输入设备名称" style={{width: 200}} value={name} allowClear onChange={onNameChange} />
          </section>
          <section className="type-info-item">
            <span className="label required">设备厂家</span>
            <Select
              allowClear
              placeholder="请选择所属公司"
              value={deviceFactory}
              style={{ width: 200 }}
              options={deviceFactoryList}
              onChange={deviceFactoryChange}
            />
          </section>
        </section>
      </section>
      <section className="create-btn">
        <Button onClick={back}>取消</Button>
        <Button onClick={createConfirm} type="primary">确定</Button>
      </section>
      <Modal open={previewOpen} title={previewTitle} footer={null} onCancel={handleCancel}>
        <img alt="example" style={{ width: '100%' }} src={previewImage} />
      </Modal>
      { messageContextHolder}
    </section>
  )
}